import type { MetaFunction } from '@remix-run/node'
import { Icon } from '#app/components/ui/icon.js'

export const meta: MetaFunction = () => {
	return [
		{ title: 'About | Crosstope' },
		{
			name: 'description',
			content:
				'A curated repository of three-dimensional structures of MHC:epitope complexes',
		},
	]
}

export default function AboutRoute() {
	return (
		<div className="container mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
			<div className="space-y-16">
				<header className="space-y-4">
					<h1 className="text-4xl font-bold leading-tight tracking-tight md:text-5xl">
						CrossTope
					</h1>
					<p className="text-muted-foreground text-lg md:text-xl">
						Structural Database for Cross-Reactivity Prediction in MHC:Peptide
						Complexes
					</p>
				</header>

				<section className="space-y-6">
					<h2 className="text-3xl font-bold tracking-tight">
						WHY | The Challenge
					</h2>
					<div className="space-y-4 text-base leading-relaxed md:text-lg">
						<p>
							Cellular immunity relies on T-cell receptors (TCR) recognizing
							peptides presented by Major Histocompatibility Complex (MHC)
							molecules. A critical—yet poorly understood—phenomenon is
							cross-reactivity: one TCR can recognize multiple different
							peptides bound to the same MHC allele. This mechanism drives
							heterologous immunity and molecular mimicry, but predicting which
							peptides trigger cross-reactive responses remains challenging.
						</p>
						<p>
							<strong>The gap:</strong> Existing structural databases lack
							experimental validation, reproducible methods, and integrated
							immunogenicity data, limiting their application in vaccine design
							and immunotherapy.
						</p>
					</div>
				</section>

				<section className="space-y-6">
					<h2 className="text-3xl font-bold tracking-tight">
						WHAT | Our Solution
					</h2>
					<div className="space-y-4 text-base leading-relaxed md:text-lg">
						<p>
							CrossTope is a highly curated repository of three-dimensional
							structures of peptide:MHC class I complexes (pMHC-I), combining
							crystallographic data with large-scale in silico modeling using
							our validated D1-EM-D2 approach.
						</p>
						<p>
							<strong>Key differentiators:</strong>
						</p>
						<ul>
							<li>
								<strong>Experimental validation required:</strong> Every epitope
								must demonstrate CTL response in vitro and/or in vivo
							</li>
							<li>
								<strong>Manual curation:</strong> Each complex includes curated
								immunogenicity data and cross-reactivity relationships verified
								from literature
							</li>
							<li>
								<strong>Continuously expanding:</strong> Growing collection of
								non-redundant complexes across human (HLA-A02:01, HLA-B27:05)
								and murine (H-2-Db, H-2-Kb) alleles
							</li>
							<li>
								<strong>Integrated access:</strong> Direct links to IEDB, NCBI,
								and UniProt for comprehensive epitope information
							</li>
						</ul>
						<p>
							<strong>Available data:</strong>
						</p>
						<ul>
							<li>Structural coordinates (.pdb format)</li>
							<li>
								Topological and charge distribution maps of TCR-interacting
								surfaces (−5/+5 and −10/+10 kT)
							</li>
							<li>Immunological background with cross-reactivity evidence</li>
						</ul>
					</div>
				</section>

				<section className="space-y-6">
					<h2 className="text-3xl font-bold tracking-tight">
						HOW | Methodology & Applications
					</h2>
					<div className="space-y-4 text-base leading-relaxed md:text-lg">
						<p>
							<strong>D1-EM-D2 Structural Modeling:</strong>
						</p>
						<p>
							Our approach combines molecular docking (D1), energy minimization
							(EM), and refinement docking (D2) to reproduce crystallographic
							structures with high accuracy (mean RMSD 0.882 Å for Cα atoms).
							This method successfully identified molecular features explaining
							immunogenicity variation among HCV-derived epitope variants,
							revealing shared charge distribution patterns in
							immune-stimulating complexes.
						</p>
						<p>
							<strong>Methodological references:</strong>
						</p>
						<ul>
							<li>
								<a
									href="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0010353"
									target="_blank"
									rel="noopener noreferrer"
									className="hover:text-foreground underline underline-offset-4 transition-colors"
								>
									Antunes DA, et al. (2010) - Structural allele-specific
									patterns in MHC-I cleft. PLoS ONE 5(5):e10353
								</a>
							</li>
							<li>
								<a
									href="https://www.nature.com/articles/srep18413"
									target="_blank"
									rel="noopener noreferrer"
									className="hover:text-foreground underline underline-offset-4 transition-colors"
								>
									Rigo MM, et al. (2015) - DockTope: Automated pMHC-I modelling.
									Sci Rep 5:18413
								</a>
							</li>
						</ul>
						<p>
							<strong>Practical applications:</strong>
						</p>
						<ul>
							<li>
								<strong>Cross-reactivity prediction:</strong> Cluster
								structurally similar epitopes to identify potential
								cross-reactive targets
							</li>
							<li>
								<strong>Viral escape analysis:</strong> Evaluate mutations at
								structural level to understand immune evasion
							</li>
							<li>
								<strong>Immunotherapy optimization:</strong> Improve tumor
								antigen immunogenicity through rational design
							</li>
							<li>
								<strong>Vaccine development:</strong> Identify conserved
								structural features across pathogen variants
							</li>
						</ul>
						<p>
							<strong>Experimental validation:</strong> Our structure-based
							predictions identified cross-reactive HCV epitope variants with
							minimal sequence identity, later confirmed experimentally with
							lymphocytes from infected patients and vaccinated
							individuals—demonstrating the power of structural over
							sequence-based approaches.
						</p>
					</div>
				</section>

				<section className="space-y-6">
					<h2 className="text-3xl font-bold tracking-tight">
						Access & Integration
					</h2>
					<div className="space-y-4 text-base leading-relaxed md:text-lg">
						<p>
							<strong>Web interface:</strong> https://crosstope.com.br/
						</p>
						<p>
							<strong>Search by:</strong> MHC allele, epitope sequence, source
							organism
						</p>
						<p>
							<strong>Compare:</strong> Side-by-side structural comparison of
							any two epitopes
						</p>
						<p>
							<strong>Cited by:</strong>
						</p>
						<ul>
							<li>
								<a
									href="https://www.frontiersin.org/journals/immunology/articles/10.3389/fimmu.2022.930590/full"
									target="_blank"
									rel="noopener noreferrer"
									className="hover:text-foreground underline underline-offset-4 transition-colors"
								>
									Mendes et al. (2022) - MatchTope tool for electrostatic
									potential-based cross-reactivity prediction
								</a>
							</li>
							<li>
								<a
									href="https://www.frontiersin.org/journals/immunology/articles/10.3389/fimmu.2017.01210/full"
									target="_blank"
									rel="noopener noreferrer"
									className="hover:text-foreground underline underline-offset-4 transition-colors"
								>
									Antunes et al. (2017) - Structural interpretation of T-cell
									cross-reactivity for TCR-based cancer immunotherapy
								</a>
							</li>
							<li>
								<a
									href="https://www.frontiersin.org/journals/immunology/articles/10.3389/fimmu.2022.931372/full"
									target="_blank"
									rel="noopener noreferrer"
									className="hover:text-foreground underline underline-offset-4 transition-colors"
								>
									Antonio et al. (2022) - Identification of SARS-CoV-2 viral
									immunogenic footprints conferring cross-protection
								</a>
							</li>
							<li>
								<a
									href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6952977/"
									target="_blank"
									rel="noopener noreferrer"
									className="hover:text-foreground underline underline-offset-4 transition-colors"
								>
									Vianna et al. (2019) - pMHC structural comparisons for vaccine
									development
								</a>
							</li>
							<li>
								Cited in reviews on T-cell cross-reactivity mechanisms and
								heterologous immunity
							</li>
						</ul>
					</div>
				</section>

				<section className="space-y-8">
					<h2 className="text-3xl font-bold tracking-tight">Team</h2>
					<div className="grid grid-cols-1 gap-6 md:grid-cols-3">
						<div className="rounded-lg border p-6">
							<div className="flex flex-col items-center text-center">
								<img
									src="/img/p_fioravanti.jpg"
									alt="Gustavo Fioravanti Vieira"
									className="mb-4 h-24 w-24 rounded-full object-cover"
								/>
								<h3 className="mb-1 text-lg font-semibold">
									Gustavo Fioravanti Vieira
								</h3>
								<p className="text-muted-foreground mb-4 text-sm">
									Main Investigator
								</p>
								<div className="flex gap-3">
									<a
										href="https://www.linkedin.com/in/gustavo-fioravanti-vieira-34337360/"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:border-foreground hover:text-foreground inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors"
										title="LinkedIn Profile"
									>
										<Icon name="linkedin-logo" className="h-4 w-4" />
										LinkedIn
									</a>
								</div>
							</div>
						</div>

						<div className="rounded-lg border p-6">
							<div className="flex flex-col items-center text-center">
								<img
									src="/img/p_bragatte_github.jpg"
									alt="Marcelo A. S. Bragatte"
									className="mb-4 h-24 w-24 rounded-full object-cover"
								/>
								<h3 className="mb-1 text-lg font-semibold">
									Marcelo A. S. Bragatte
								</h3>
								<p className="text-muted-foreground mb-4 text-sm">
									Database Maintainer & Researcher
								</p>
								<div className="flex gap-3">
									<a
										href="https://www.linkedin.com/in/bragatte/"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:border-foreground hover:text-foreground inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors"
										title="LinkedIn Profile"
									>
										<Icon name="linkedin-logo" className="h-4 w-4" />
										LinkedIn
									</a>
									<a
										href="https://github.com/BragatteMAS"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:border-foreground hover:text-foreground inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors"
										title="GitHub Profile"
									>
										<Icon name="github-logo" className="h-4 w-4" />
										GitHub
									</a>
								</div>
							</div>
						</div>

						<div className="rounded-lg border p-6">
							<div className="flex flex-col items-center text-center">
								<img
									src="/img/p_frontendwizard.jpg"
									alt="frontendwizard"
									className="mb-4 h-24 w-24 rounded-full object-cover"
								/>
								<h3 className="mb-1 text-lg font-semibold">frontendwizard</h3>
								<p className="text-muted-foreground mb-4 text-sm">Developer</p>
								<div className="flex gap-3">
									<a
										href="https://github.com/frontendwizard"
										target="_blank"
										rel="noopener noreferrer"
										className="hover:border-foreground hover:text-foreground inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors"
										title="GitHub Profile"
									>
										<Icon name="github-logo" className="h-4 w-4" />
										GitHub
									</a>
								</div>
							</div>
						</div>
					</div>
				</section>

				<section className="space-y-6">
					<h2 className="text-3xl font-bold tracking-tight">Research Group</h2>
					<div className="space-y-4 text-base leading-relaxed md:text-lg">
						<p>
							<strong>
								NBLI (Núcleo de Bioinformática do Laboratório de Imunogenética)
							</strong>{' '}
							is a research team focused on identifying immunological mechanisms
							triggered by viral infections and potential viral targets.
						</p>
					</div>
				</section>

				<section className="space-y-8">
					<h2 className="text-3xl font-bold tracking-tight">
						Financial Support
					</h2>
					<div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
						<a
							href="https://www.gatesfoundation.org/"
							target="_blank"
							rel="noopener noreferrer"
							className="transition-opacity hover:opacity-70"
							title="Bill & Melinda Gates Foundation"
						>
							<img
								src="/img/gates-foundation-logo.svg"
								alt="Bill & Melinda Gates Foundation"
								className="h-16 w-auto md:h-20"
							/>
						</a>
						<a
							href="https://www.gov.br/cnpq"
							target="_blank"
							rel="noopener noreferrer"
							className="transition-opacity hover:opacity-70"
							title="CNPq - Conselho Nacional de Desenvolvimento Científico e Tecnológico"
						>
							<img
								src="/img/cnpq-logo-new.png"
								alt="CNPq"
								className="h-16 w-auto md:h-20"
							/>
						</a>
						<a
							href="https://www.gov.br/capes"
							target="_blank"
							rel="noopener noreferrer"
							className="transition-opacity hover:opacity-70"
							title="CAPES - Coordenação de Aperfeiçoamento de Pessoal de Nível Superior"
						>
							<img
								src="/img/capes-logo-new.png"
								alt="CAPES"
								className="h-16 w-auto md:h-20"
							/>
						</a>
					</div>
				</section>

				<section className="space-y-6">
					<h2 className="text-3xl font-bold tracking-tight">How to Cite</h2>
					<div className="space-y-6 text-base leading-relaxed md:text-lg">
						<p>
							<strong>Primary reference:</strong>
						</p>
						<blockquote className="border-l-4 pl-4">
							<p>
								Sinigaglia M, Antunes DA, Rigo MM, Chies JAB, Vieira GF.
								CrossTope: a curate repository of 3D structures of immunogenic
								peptide: MHC complexes. Database (Oxford). 2013;2013:bat002.
								doi: 10.1093/database/bat002
							</p>
						</blockquote>
						<p>
							<a
								href="https://academic.oup.com/database/article/doi/10.1093/database/bat002/329916"
								target="_blank"
								rel="noopener noreferrer"
								className="hover:text-foreground inline-flex items-center gap-2 text-sm transition-colors"
							>
								<Icon name="file-text" className="h-4 w-4" />
								Read the full paper
							</a>
						</p>
						<p>
							<strong>BibTeX:</strong>
						</p>
						<pre className="bg-muted overflow-x-auto rounded-md p-4 text-sm">
							<code>{`@article{sinigaglia2013crosstope,
  title={CrossTope: a curate repository of 3D structures of immunogenic peptide: MHC complexes},
  author={Sinigaglia, Marialva and Antunes, Dinler A and Rigo, Maurício M and Chies, José AB and Vieira, Gustavo F},
  journal={Database},
  volume={2013},
  pages={bat002},
  year={2013},
  publisher={Oxford University Press},
  doi={10.1093/database/bat002}
}`}</code>
						</pre>
						<p>
							<strong>PubMed:</strong> PMID: 23396301
							<br />
							<strong>PMC:</strong> PMC3567486
						</p>
					</div>
				</section>

				<section className="space-y-8">
					<h2 className="text-3xl font-bold tracking-tight">
						Related Projects
					</h2>
					<div className="flex justify-center">
						<a
							href="http://tools.iedb.org/docktope/"
							target="_blank"
							rel="noopener noreferrer"
							className="hover:border-foreground group max-w-md rounded-lg border p-6 transition-colors"
						>
							<div className="mb-4 flex items-center gap-4">
								<img
									src="/img/logo_docktope.png"
									alt="DockTope"
									className="h-16 w-16 rounded object-cover"
								/>
								<h3 className="group-hover:text-foreground text-xl font-semibold transition-colors">
									DockTope
								</h3>
							</div>
							<p className="text-muted-foreground text-sm">
								Molecular docking tool for peptide-MHC complex prediction
							</p>
						</a>
					</div>
				</section>
			</div>
		</div>
	)
}
